import React, { useState, useRef, use } from 'react';
import { Fullscreen, Favorite, FavoriteBorder, Reply } from '@mui/icons-material';
import { RiVideoAiFill } from 'react-icons/ri';
import { type UserVideo } from '@/api/videoApi';
import { useToggleVideoFavourite } from '@/hooks/querry_hooks/common/useFavouriteQuery';
// do import { useToggleVideoArchive } from '@/hooks/querry_hooks/common/useArchiveQuery';
import { DeviceContext } from '@/hooks/common/DeviceContext';
import { getEmailBadgeStyle } from '@/components/ui/styles/badgeStyles';

interface SimpleVideoThumbnailProps {
  video: UserVideo;
  // onPinVideo?: (video: UserVideo) => void;
  // onUnpinVideo?: (video: UserVideo) => void;
  onVideoClick: (video: UserVideo) => void;
  getStatusBadge: (status?: string) => React.ReactNode;
  // isPinnedSection?: boolean;
  isFavouritesPage?: boolean;
  isArchivesPage?: boolean;
  onFavouriteSuccess?: (isFavourited: boolean) => void;
  // onArchiveSuccess?: (isArchived: boolean) => void;
  // isAdmin?: boolean;
  onAttachVideoLastFrame?: (video: UserVideo) => Promise<void>;
}

// Commented out helper function for pin/unpin logic
// const handlePinToggle = (
//   video: UserVideo,
//   onPinVideo?: (video: UserVideo) => void,
//   onUnpinVideo?: (video: UserVideo) => void
// ) => {
//   if (video.is_pinned && onUnpinVideo) {
//     onUnpinVideo(video);
//   } else if (!video.is_pinned && onPinVideo) {
//     onPinVideo(video);
//   }
// };

// Helper component for the pin button
const PinButton: React.FC<{
  isFavorited: boolean;
  onToggleFavorite: (e: React.MouseEvent) => void;
  isArchivesPage?: boolean;
  onCopyLink: (e: React.MouseEvent) => void;
  copied: boolean;
  onAttachLastFrame?: (e: React.MouseEvent) => void;
}> = ({ isFavorited, onToggleFavorite, isArchivesPage, onCopyLink, copied, onAttachLastFrame }) => (
  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
    <div className="flex items-center gap-3">
      {/* Attach Last Frame Button - Only show if handler is provided */}
      {onAttachLastFrame && (
        <button
          type="button"
          onClick={onAttachLastFrame}
          className="bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all duration-200 hover:scale-110 pointer-events-auto"
          title="Attach last frame to Videos tab"
          aria-label="Attach last frame to Videos tab"
        >
          <RiVideoAiFill className="text-white text-xl" />
        </button>
      )}

      {/* Favorite Button - Always show for all users EXCEPT on archives page */}
      {!isArchivesPage && <FavoriteButton isFavorited={isFavorited} onToggleFavorite={onToggleFavorite} />}

      {/* Share Button - alongside favourite */}
      <ShareButton onCopyLink={onCopyLink} copied={copied} />
    </div>
  </div>
);

// Helper component for the fullscreen button
const FullscreenButton: React.FC<{
  onClick: (e: React.MouseEvent) => void;
}> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute bottom-3 right-3 bg-black bg-opacity-60 hover:bg-opacity-80 rounded-lg p-2 transition-all duration-200 hover:scale-110 opacity-0 group-hover:opacity-100 z-10 pointer-events-auto"
    title="Open in fullscreen"
    aria-label="Open in fullscreen"
  >
    <Fullscreen className="text-white text-xl" />
  </button>
);

// Helper component for the share button (copy link)
const ShareButton: React.FC<{
  onCopyLink: (e: React.MouseEvent) => void;
  copied: boolean;
}> = ({ onCopyLink, copied }) => (
  <button
    type="button"
    onClick={onCopyLink}
    className="bg-black bg-opacity-50 hover:bg-opacity-70 transition-all duration-200 hover:scale-110 pointer-events-auto flex items-center justify-center"
    style={{ borderRadius: '16px', padding: '12px' }}
    title={copied ? "Copied!" : "Copy link"}
    aria-label={copied ? "Copied!" : "Copy link"}
  >
    {copied ? (
      <span className="text-white text-xs font-medium leading-none">Copied!</span>
    ) : (
      <Reply className="text-white text-lg" style={{ transform: 'scaleX(-1)' }} />
    )}
  </button>
);

// Helper component for the favorite button
const FavoriteButton: React.FC<{
  isFavorited: boolean;
  onToggleFavorite: (e: React.MouseEvent) => void;
}> = ({ isFavorited, onToggleFavorite }) => (
  <button
    type="button"
    onClick={onToggleFavorite}
    className="bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all duration-200 hover:scale-110 pointer-events-auto"
    title={isFavorited ? "Remove from favorites" : "Add to favorites"}
    aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
  >
    {isFavorited ? (
      <Favorite className="text-white text-lg" />
    ) : (
      <FavoriteBorder className="text-white text-lg" />
    )}
  </button>
);

// Commented out Archive Button component
// const ArchiveButton: React.FC<{
//   isArchived: boolean;
//   onToggleArchive: (e: React.MouseEvent) => void;
// }> = ({ isArchived, onToggleArchive }) => (
//   <button
//     type="button"
//     onClick={onToggleArchive}
//     className="bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all duration-200 hover:scale-110 pointer-events-auto"
//     title={isArchived ? "Remove from archive" : "Add to archive"}
//     aria-label={isArchived ? "Remove from archive" : "Add to archive"}
//   >
//     {isArchived ? (
//       <Archive className="text-white text-lg" />
//     ) : (
//       <ArchiveOutlined className="text-white text-lg" />
//     )}
//   </button>
// );

const SimpleVideoThumbnail: React.FC<SimpleVideoThumbnailProps> = ({
  video,
  // onPinVideo,
  // onUnpinVideo,
  onVideoClick,
  getStatusBadge,
  // isPinnedSection = false,
  isFavouritesPage = false,
  isArchivesPage = false,
  onFavouriteSuccess,
  // onArchiveSuccess,
  // isAdmin = false,
  onAttachVideoLastFrame,
}) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isFavorited, setIsFavorited] = useState(video.is_favourited ?? false);
  const [copied, setCopied] = useState(false);
  // do const [isArchived, setIsArchived] = useState(video.is_archived ?? false);
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fullscreenVideoRef = useRef<HTMLVideoElement>(null);
  
  // Get the mutation hook for toggling favourites
  const toggleFavouriteMutation = useToggleVideoFavourite(
    (isFavourited: boolean) => {
      if (onFavouriteSuccess) {
        onFavouriteSuccess(isFavourited);
      }
    }
  );
  
  // Commented out archive mutation hook
  // const toggleArchiveMutation = useToggleVideoArchive(
  //   (isArchived: boolean) => {
  //     // Update local state when archive succeeds
  //     setIsArchived(isArchived);
  //     // Call the success callback if provided
  //     if (onArchiveSuccess) {
  //       onArchiveSuccess(isArchived);
  //     }
  //   }
  // );

  // Update local state when video prop changes
  // If on favourites page, always show as favorited
  React.useEffect(() => {
    setIsFavorited(isFavouritesPage ? true : (video.is_favourited ?? false));
  }, [video.is_favourited, isFavouritesPage]);
  
  // Commented out archive status update
  // React.useEffect(() => {
  //   setIsArchived(video.is_archived ?? false);
  // }, [video.is_archived]);

  // Check if video URL is available for playback
  const canPlayVideo = Boolean(video.video_url && video.video_url !== '');

  // Handle favorite toggle
  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Use the mutation to toggle favourite (handles optimistic updates automatically)
    toggleFavouriteMutation.mutate(video.id);
  };

  // Handle copy link
  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard.writeText(globalThis.location.origin + '/dashboard?video=' + video.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle attach last frame
  const handleAttachLastFrame = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (onAttachVideoLastFrame) {
      await onAttachVideoLastFrame(video);
    }
  };

  // Commented out archive toggle handler
  // const handleToggleArchive = async (e: React.MouseEvent) => {
  //   e.stopPropagation();
  //   e.preventDefault();
  //   
  //   // Use the mutation to toggle archive (handles optimistic updates automatically)
  //   toggleArchiveMutation.mutate(video.id);
  // };

  // Handle fullscreen mode
  const handleFullscreen = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!video.video_url) return;

    // Store scroll position before entering fullscreen
    const scrollPosition = window.scrollY;

    // Create a fullscreen video player
    const videoElement = document.createElement('video');
    videoElement.src = video.video_url;
    videoElement.controls = true;
    videoElement.autoplay = true;
    videoElement.loop = true;
    videoElement.style.cssText = 'width: 100vw; height: 100vh; object-fit: contain; background: black;';
    
    document.body.appendChild(videoElement);
    
    // Store reference for cleanup
    fullscreenVideoRef.current = videoElement;

    // Handle cleanup when exiting fullscreen
    const handleFullscreenChange = () => {
      const isInFullscreen = !!(
        document.fullscreenElement || 
        (document as any).webkitFullscreenElement || 
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      
      if (!isInFullscreen && document.body.contains(videoElement)) {
        videoElement.pause();
        videoElement.remove();
        fullscreenVideoRef.current = null;
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
        document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        
        // Restore scroll position after exiting fullscreen
        window.scrollTo(0, scrollPosition);
      }
    };

    // Add fullscreen change listeners
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Try to enter native fullscreen mode on the video element
    try {
      if (videoElement.requestFullscreen) {
        await videoElement.requestFullscreen();
      } else if ((videoElement as any).webkitRequestFullscreen) {
        await (videoElement as any).webkitRequestFullscreen();
      } else if ((videoElement as any).mozRequestFullScreen) {
        await (videoElement as any).mozRequestFullScreen();
      } else if ((videoElement as any).msRequestFullscreen) {
        await (videoElement as any).msRequestFullscreen();
      }
    } catch (error) {
      // Fullscreen request failed - clean up
      console.warn('Fullscreen mode not available:', error);
      videoElement.remove();
      fullscreenVideoRef.current = null;
    }
  };

  // Detect when component is in viewport (for lazy loading)
  React.useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setIsInViewport(entry.isIntersecting);
        }
      },
      { rootMargin: '200px' } // Start preparing video slightly before entering viewport
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleVideoHover = (play: boolean) => {
    if (!canPlayVideo || !videoRef.current) return;

    if (play) {
      // Only create video player on first hover (lazy initialization)
      setShouldLoadVideo(true);
      
      // Wait a tick for video to be created, then load and play
      setTimeout(() => {
        if (videoRef.current) {
          if (!videoLoaded) {
            videoRef.current.load();
          }
          videoRef.current.play().catch(() => {
            // Video autoplay failed - silently ignore
          });
        }
      }, 0);
    } else if (videoLoaded && videoRef.current) {
      // On mouse leave: pause and reset
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleVideoLoadedData = () => {
    setVideoLoaded(true);
  };

  // Clean up video element when component unmounts or leaves viewport
  React.useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
        videoRef.current.load(); // Force release of media resources
      }
    };
  }, []);

  // Unload video when it leaves viewport to free up WebMediaPlayer slots
  React.useEffect(() => {
    if (!isInViewport && shouldLoadVideo && videoRef.current) {
      // If video has been loaded but is now off-screen, unload it
      const wasPlaying = !videoRef.current.paused;
      if (!wasPlaying) {
        // Only unload if not playing (don't interrupt hover playback)
        videoRef.current.pause();
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
        setShouldLoadVideo(false);
        setVideoLoaded(false);
      }
    }
  }, [isInViewport, shouldLoadVideo]);

  // Get aspect ratio for styling - default to 16:9 if not specified
  const aspectRatio = video.aspect_ratio || '16:9';
  const isPortrait = aspectRatio === '9:16' || aspectRatio === '3:4' || aspectRatio === '9:21';
  // Keep tiles at 16:9, but use object-contain for portrait videos to show them correctly
  const objectFitClass = isPortrait ? 'object-contain' : 'object-cover';

  const { isMobile } = use(DeviceContext) ?? { isMobile: false };

  const emailBadgeStyle = getEmailBadgeStyle(isMobile);

  return (
    <div
      ref={ref}
      className="rounded-xl overflow-hidden hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl relative bg-gray-900 group"
      style={{ aspectRatio: '16/9' }}
    >
      {video.video_url ? (
        /* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/prefer-tag-over-role */
        <div 
          role="button"
          tabIndex={0}
          className="w-full h-full relative cursor-pointer outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
          onMouseEnter={() => canPlayVideo && handleVideoHover(true)}
          onMouseLeave={() => canPlayVideo && handleVideoHover(false)}
          onClick={() => onVideoClick(video)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onVideoClick(video);
            }
          }}
        >
          {/* Conditional rendering: Video element only when in viewport and needed */}
          {canPlayVideo ? (
            <>
              {/* Only render video element when in viewport OR already loaded (to prevent 75 player limit) */}
              {(isInViewport || shouldLoadVideo) && (
                <video
                  ref={videoRef}
                  className={objectFitClass}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    height: '100%',
                    transform: 'translateX(-50%)'
                  }}
                  muted
                  loop
                  preload="none"
                  poster={video.thumbnail_url || undefined}
                  onLoadedData={handleVideoLoadedData}
                >
                  <source src={video.video_url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}

              {/* Show poster image when video element not rendered */}
              {!isInViewport && !shouldLoadVideo && video.thumbnail_url && (
                <img
                  src={video.thumbnail_url}
                  alt={video.title}
                  className={objectFitClass}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    height: '100%',
                    transform: 'translateX(-50%)'
                  }}
                />
              )}

              {/* Loading overlay - only show if video hasn't loaded yet and no thumbnail */}
              {!videoLoaded && !video.thumbnail_url && (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                  <div className="flex flex-col items-center">
                    {/* Spinner */}
                    <svg
                      className="animate-spin h-10 w-10 text-white mb-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <p className="text-white text-sm font-medium">Loading...</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Show thumbnail image until video URL loads */
            <img
              src={video.thumbnail_url}
              alt={video.title || 'Video thumbnail'}
              className={objectFitClass}
              style={{ 
                position: 'absolute', 
                top: 0, 
                left: '50%', 
                height: '100%',
                transform: 'translateX(-50%)'
              }}
            />
          )}

          {/* Video Overlay with User Info */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex flex-col justify-between">
            {/* Top section with model, status, and user email badges */}
            <div className="flex items-start justify-between gap-2" style={{ padding: '10px' }}>
              {/* Model badge with aspect ratio on the left */}
              <div 
                className="bg-gray-800 bg-opacity-90 text-white flex items-center"
                style={{
                  borderRadius: '30px',
                  paddingTop: '5px',
                  paddingRight: '13px',
                  paddingBottom: '5px',
                  paddingLeft: '13px',
                  height: '30px',
                  minWidth: '140px',
                  justifyContent: 'space-between',
                  fontFamily: 'Maven Pro',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '18px',
                  letterSpacing: '0%',
                  gap: '4px'
                }}
              >
                <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {(video.model || 'Veo 3').replace('Veo 3.1', 'Veo3.1').replace('Veo 3', 'Veo3')}
                </span>
                <svg 
                  width="16" 
                  height="9" 
                  viewBox="0 0 16 9" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <rect x="0.5" y="0.5" width="15" height="8" rx="0.5" stroke="white" fill="transparent"/>
                </svg>
                <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {video.aspect_ratio || '16:9'}
                </span>
              </div>

              {/* Status badge */}
              <div style={{ width: '90px' }}>
                {getStatusBadge(video.status)}
              </div>
              
              {/* User email badge */}
              <div 
                className="bg-gray-800 bg-opacity-90 text-white"
                style={emailBadgeStyle}
              >
                <span className="truncate" style={{ maxWidth: '103px' }}>
                  {video.user_email}
                </span>
              </div>
            </div>

            {/* Favourite and Share buttons - appear on hover in center */}
            <PinButton
              isFavorited={isFavorited}
              onToggleFavorite={handleToggleFavorite}
              isArchivesPage={isArchivesPage}
              onCopyLink={handleCopyLink}
              copied={copied}
              onAttachLastFrame={onAttachVideoLastFrame ? handleAttachLastFrame : undefined}
            />

            {/* Fullscreen Button - appears on hover in bottom right */}
            {canPlayVideo && <FullscreenButton onClick={handleFullscreen} />}
            
            {/* Original/Extended badge - bottom left corner */}
            <div 
              className="absolute bottom-3 left-3 bg-gray-800 bg-opacity-90 text-white flex items-center justify-center"
              style={{
                borderRadius: '30px',
                paddingTop: '5px',
                paddingRight: '13px',
                paddingBottom: '5px',
                paddingLeft: '13px',
                height: '30px',
                fontFamily: 'Maven Pro',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '18px',
                whiteSpace: 'nowrap',
              }}
            >
              {video.extended ? 'Extended' : 'Original'}
            </div>
          </div>
        </div>
      ) : (
        // Fallback for videos without URL
        <div className="w-full h-full flex items-center justify-center bg-gray-300">
          <div className="text-center">
            <svg className="w-12 h-12 text-gray-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-500 text-sm">Video loading...</p>
            <p className="text-gray-600 text-xs mt-1">{video.user_email}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleVideoThumbnail;
